/**
 * Import orchestrator (F3、Phase 5)。
 *
 * 対応フォーマット:
 *   - OoKey native JSON (`{ format: 'ookey', items: [...] }`)
 *   - Bitwarden JSON (encrypted: false のみ)
 *
 * 流れ:
 *   1. `parseImportFile(file)` → ImportPreview を返す
 *   2. UI で preview を表示し、ユーザが「import」 button を押す
 *   3. `executeImport(preview)` で folder 作成 (新規分のみ) + vault item 一括作成
 *
 * SK 取得 / 暗号化は既存 `vaultFlow.createVaultItemEncrypted` を使うため
 * crypto handling は本ファイルでは行わない (ゼロ知識を維持)。
 */

import { sessionStore } from '@features/auth/services/sessionStore'
import { createFolderEncrypted } from '@features/vault/services/folderFlow'
import { createVaultItemEncrypted, loadVaultList } from '@features/vault/services/vaultFlow'
import type {
  UriMatchType,
  VaultCustomField,
  VaultCustomFieldType,
  VaultItemPayload,
  VaultUri,
} from '@features/vault/types'
import {
  ImportError,
  type BitwardenExport,
  type BitwardenItem,
  type BitwardenLogin,
  type GoogleLoginRow,
  type ImportPreview,
  type ImportPreviewItem,
  type OoKeyExport,
} from '../types'

const sanitizeStr = (s: string | null | undefined): string | undefined => {
  if (typeof s !== 'string') return undefined
  const trimmed = s.trim()

  return trimmed.length > 0 ? trimmed : undefined
}

const googleRowToPayload = (row: GoogleLoginRow): VaultItemPayload | null => {
  const name = sanitizeStr(row.name) ?? sanitizeStr(row.url) ?? sanitizeStr(row.username)
  if (!name) return null

  return {
    type: 'login',
    name,
    username: sanitizeStr(row.username),
    password: sanitizeStr(row.password),
    url: sanitizeStr(row.url),
    notes: sanitizeStr(row.note),
  }
}

const looksLikeGoogleRow = (v: unknown): v is GoogleLoginRow => {
  if (typeof v !== 'object' || v === null) return false
  const o = v as Record<string, unknown>

  // Google Chrome export rows have at least one of these keys + a string value
  const candidates = ['url', 'username', 'password', 'name']

  return candidates.some(k => typeof o[k] === 'string')
}

/** Bitwarden の URI match 数値 → OoKey の UriMatchType。 */
const BW_URI_MATCH: Readonly<Record<number, UriMatchType>> = {
  0: 'base_domain',
  1: 'host',
  2: 'starts_with',
  3: 'exact',
  4: 'regex',
  5: 'never',
}

/** Bitwarden のカスタムフィールド type 数値 → OoKey の VaultCustomFieldType。 */
const BW_FIELD_TYPE: Readonly<Record<number, VaultCustomFieldType>> = {
  0: 'text',
  1: 'hidden',
  2: 'boolean',
}

/** Bitwarden login の uris[] を OoKey の VaultUri[] に変換 (空 URI は捨てる)。 */
const bwUrisToPayload = (uris: BitwardenLogin['uris']): VaultUri[] | undefined => {
  if (!uris) return undefined
  const out: VaultUri[] = []
  for (const u of uris) {
    const uri = sanitizeStr(u.uri)
    if (!uri) continue
    const match = typeof u.match === 'number' ? BW_URI_MATCH[u.match] : undefined

    out.push(match ? { uri, match } : { uri })
  }

  return out.length > 0 ? out : undefined
}

/** Bitwarden の fields[] を OoKey の VaultCustomField[] に変換 (linked 等 未対応 type はスキップ)。 */
const bwFieldsToPayload = (fields: BitwardenItem['fields']): VaultCustomField[] | undefined => {
  if (!fields) return undefined
  const out: VaultCustomField[] = []
  for (const f of fields) {
    const name = sanitizeStr(f.name)
    if (!name) continue
    const type = typeof f.type === 'number' ? BW_FIELD_TYPE[f.type] : undefined
    if (!type) continue
    out.push({ name, value: f.value ?? '', type })
  }

  return out.length > 0 ? out : undefined
}

const bitwardenItemToPayload = (item: BitwardenItem): VaultItemPayload | null => {
  const common = {
    name: item.name?.trim() ?? '',
    notes: sanitizeStr(item.notes),
    favorite: item.favorite === true,
    fields: bwFieldsToPayload(item.fields),
  }

  if (!common.name) return null

  if (item.type === 1 && item.login) {
    return {
      type: 'login',
      ...common,
      username: sanitizeStr(item.login.username),
      password: sanitizeStr(item.login.password),
      uris: bwUrisToPayload(item.login.uris),
      totpSecret: sanitizeStr(item.login.totp),
    }
  }
  if (item.type === 3 && item.card) {
    const exp =
      item.card.expMonth && item.card.expYear
        ? `${String(item.card.expMonth).padStart(2, '0')}/${String(item.card.expYear).slice(-2)}`
        : undefined

    return {
      type: 'card',
      ...common,
      cardNumber: sanitizeStr(item.card.number),
      cardHolder: sanitizeStr(item.card.cardholderName),
      cardExpiry: exp,
      cardCvv: sanitizeStr(item.card.code),
    }
  }
  if (item.type === 4 && item.identity) {
    return {
      type: 'identity',
      ...common,
      firstName: sanitizeStr(item.identity.firstName),
      lastName: sanitizeStr(item.identity.lastName),
      phone: sanitizeStr(item.identity.phone),
      email: sanitizeStr(item.identity.email),
    }
  }
  if (item.type === 2) {
    return {
      type: 'note',
      ...common,
      body: item.notes ?? '',
    }
  }

  return null
}

/**
 * CSV テキストを行列に分解する。RFC 4180 準拠の最小実装:
 *   - ダブルクォートで囲まれたフィールド内の `,` `\n` `"` ("" でエスケープ) を扱う
 *   - 行末は `\r\n` / `\n` どちらも許容
 */
const parseCsvRows = (text: string): string[][] => {
  const rows: string[][] = []
  let row: string[] = []
  let field = ''
  let inQuotes = false

  for (let i = 0; i < text.length; i++) {
    const c = text[i]

    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"'
          i++
        } else {
          inQuotes = false
        }
      } else {
        field += c
      }
      continue
    }

    if (c === '"') {
      inQuotes = true
    } else if (c === ',') {
      row.push(field)
      field = ''
    } else if (c === '\n') {
      row.push(field)
      rows.push(row)
      row = []
      field = ''
    } else if (c !== '\r') {
      field += c
    }
  }
  if (field !== '' || row.length > 0) {
    row.push(field)
    rows.push(row)
  }

  return rows
}

/**
 * CSV を ImportPreview に変換する。2 系統を扱う:
 *
 *   1. **OoKey フル CSV** (`exportFlow.buildExportCsv` 由来): `type` 列を持つ。
 *      `type` 列の値で login / card / identity / note を判定し、各固有列から
 *      payload を復元する (全 type 無損失で往復可能)。
 *   2. **一般 CSV** (Google / Bitwarden / 各種パスワードマネージャ等): `type` 列が
 *      無い。**行ごとに実データを分析して type を推定する** — カード番号 / CVV /
 *      有効期限の列があれば card、姓名 / 電話の列があれば identity、ユーザー名 /
 *      パスワード / URL があれば login、本文系の列しか無ければ note。
 *
 * ヘッダ照合は大文字小文字無視・別名を広く許容する。folder 列は CSV 経路では未対応。
 */
const csvToPreview = (text: string): ImportPreview | null => {
  const rows = parseCsvRows(text).filter(r => r.some(cell => cell.trim() !== ''))
  if (rows.length < 2) return null

  const header = rows[0]!.map(h => h.trim().toLowerCase())

  const col = (...names: string[]): number => {
    for (const n of names) {
      const idx = header.indexOf(n)
      if (idx !== -1) return idx
    }

    return -1
  }

  const cell = (r: string[], idx: number): string | undefined => {
    if (idx === -1) return undefined
    const v = r[idx]?.trim()

    return v && v.length > 0 ? v : undefined
  }

  const nameIdx = col('name', 'title', 'item name')
  const typeIdx = col('type')
  const urlIdx = col('url', 'website', 'login_uri', 'uri', 'site')
  const userIdx = col('username', 'login_username', 'user', 'login', 'account', 'email')
  const passIdx = col('password', 'login_password', 'pass')
  const noteIdx = col('notes', 'note', 'memo', 'comment', 'comments')
  const totpIdx = col('totp', 'totp secret', 'otpauth', 'otp', '2fa')
  const cardNumberIdx = col('cardnumber', 'card number', 'card_number', 'ccnumber', 'cc number')

  const cardHolderIdx = col(
    'cardholder',
    'card holder',
    'cardholdername',
    'cardholder name',
    'name on card',
  )

  const cardExpiryIdx = col('cardexpiry', 'expiry', 'expiration', 'exp', 'expiration date')
  const cardCvvIdx = col('cardcvv', 'cvv', 'cvc', 'security code', 'card_code', 'cardcode')
  const firstNameIdx = col('firstname', 'first name', 'given name')
  const lastNameIdx = col('lastname', 'last name', 'surname', 'family name')
  const phoneIdx = col('phone', 'phone number', 'tel', 'mobile')
  const emailIdx = col('email', 'e-mail')
  const bodyIdx = col('body', 'content', 'secure note', 'securenote')

  const hasType = typeIdx !== -1

  /** 13–19 桁の数字列が Luhn チェックを通るか判定する。 */
  const isLuhnValid = (digits: string): boolean => {
    if (digits.length < 13 || digits.length > 19) return false
    let sum = 0
    let double = false
    for (let i = digits.length - 1; i >= 0; i--) {
      let d = digits.charCodeAt(i) - 48
      if (d < 0 || d > 9) return false
      if (double) {
        d *= 2
        if (d > 9) d -= 9
      }
      sum += d
      double = !double
    }

    return sum % 10 === 0
  }

  /**
   * 行の中からカード番号らしい値を探す。空白 / ハイフン除去後に 13–19 桁の数字で
   * Luhn チェックを通る値を返す。username / password 列はパスワード等の偶然一致を
   * 避けるため対象外とする。専用のカード番号列が無い CSV でも card を拾えるようにする。
   */
  const findCardNumber = (r: string[]): string | undefined => {
    for (let i = 0; i < r.length; i++) {
      if (i === userIdx || i === passIdx) continue
      const v = r[i]?.trim()
      if (!v) continue
      const digits = v.replace(/[\s-]/gu, '')
      if (/^\d+$/u.test(digits) && isLuhnValid(digits)) return v
    }

    return undefined
  }

  // type 列も認識できる内容列も無ければ CSV として解釈不能。
  const contentCols = [
    userIdx,
    passIdx,
    urlIdx,
    totpIdx,
    cardNumberIdx,
    cardCvvIdx,
    cardExpiryIdx,
    firstNameIdx,
    lastNameIdx,
    phoneIdx,
    bodyIdx,
    noteIdx,
  ]

  if (!hasType && contentCols.every(i => i === -1)) return null

  /** type 列が無い CSV 向け: 行に入っている実データから type を推定する。 */
  const detectRowType = (r: string[]): VaultItemPayload['type'] => {
    if (cell(r, cardNumberIdx) ?? cell(r, cardCvvIdx) ?? cell(r, cardExpiryIdx)) return 'card'
    if (findCardNumber(r)) return 'card'
    if (cell(r, firstNameIdx) ?? cell(r, lastNameIdx) ?? cell(r, phoneIdx)) return 'identity'
    if (cell(r, userIdx) ?? cell(r, passIdx) ?? cell(r, urlIdx) ?? cell(r, totpIdx)) return 'login'
    if (cell(r, bodyIdx) ?? cell(r, noteIdx)) return 'note'

    return 'login'
  }

  const items: ImportPreviewItem[] = []
  for (let i = 1; i < rows.length; i++) {
    const r = rows[i]!

    const name =
      cell(r, nameIdx) ??
      cell(r, urlIdx) ??
      cell(r, userIdx) ??
      cell(r, cardHolderIdx) ??
      cell(r, firstNameIdx)

    if (!name) continue

    const itemType: VaultItemPayload['type'] = hasType
      ? normalizeType(cell(r, typeIdx))
      : detectRowType(r)

    const notes = cell(r, noteIdx)
    let payload: VaultItemPayload

    if (itemType === 'card') {
      payload = {
        type: 'card',
        name,
        notes,
        cardNumber: cell(r, cardNumberIdx) ?? findCardNumber(r),
        cardHolder: cell(r, cardHolderIdx),
        cardExpiry: cell(r, cardExpiryIdx),
        cardCvv: cell(r, cardCvvIdx),
      }
    } else if (itemType === 'identity') {
      payload = {
        type: 'identity',
        name,
        notes,
        firstName: cell(r, firstNameIdx),
        lastName: cell(r, lastNameIdx),
        phone: cell(r, phoneIdx),
        email: cell(r, emailIdx),
      }
    } else if (itemType === 'note') {
      // body 専用列があればそれを、無ければ note/memo 列を本文に使う
      // (後者の場合 notes 列と重複しないよう notes は省く)。
      payload = {
        type: 'note',
        name,
        notes: bodyIdx !== -1 ? notes : undefined,
        body: cell(r, bodyIdx) ?? cell(r, noteIdx) ?? '',
      }
    } else {
      payload = {
        type: 'login',
        name,
        notes,
        username: cell(r, userIdx),
        password: cell(r, passIdx),
        url: cell(r, urlIdx),
        totpSecret: cell(r, totpIdx),
      }
    }

    items.push({ name, payload, folderId: null })
  }

  if (items.length === 0) return null

  return { format: 'csv', items, newFolderNames: [] }
}

/** `type` 列の値を OoKey の 4 種別に正規化する (未知の値は login 扱い)。 */
const normalizeType = (raw: string | undefined): VaultItemPayload['type'] => {
  const v = (raw ?? '').toLowerCase()
  if (v === 'card') return 'card'
  if (v === 'identity') return 'identity'
  if (v === 'note') return 'note'

  return 'login'
}

/**
 * テキストを parse して ImportPreview を返す。失敗時は ImportError を throw。
 * folder 解決は preview 時に `existingFolders` (現在の OoKey folder 一覧) と照合し、
 * 既存と一致するものは既存 id を、それ以外は null + `newFolderNames` に積む。
 */
export const parseImportFile = (
  rawText: string,
  existingFolders: readonly { id: string; name: string }[],
): ImportPreview => {
  // 先頭 BOM (U+FEFF) を除去する。BOM 付き CSV (Excel / 一部のパスワード
  // マネージャ書き出し) はヘッダ先頭列名が "U+FEFF + name" になり、列マッチが
  // 全て外れて取り込み結果がずれる原因になるため。
  const text = rawText.replace(/^\uFEFF/u, '')

  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch {
    // JSON でなければ CSV として解釈を試みる
    const csv = csvToPreview(text)
    if (csv) return csv
    throw new ImportError('invalid_json')
  }

  // Google: top-level array of login rows
  if (Array.isArray(parsed) && parsed.length > 0 && parsed.every(looksLikeGoogleRow)) {
    const items: ImportPreviewItem[] = []
    for (const row of parsed) {
      const payload = googleRowToPayload(row)
      if (payload) items.push({ name: payload.name, payload, folderId: null })
    }
    if (items.length === 0) throw new ImportError('empty')

    return { format: 'google', items, newFolderNames: [] }
  }

  if (typeof parsed !== 'object' || parsed === null) {
    throw new ImportError('invalid_json')
  }

  const obj = parsed as Record<string, unknown>

  // Google: `{ passwords: [...] }` shape
  if (
    Array.isArray(obj.passwords) &&
    obj.passwords.length > 0 &&
    obj.passwords.every(looksLikeGoogleRow) &&
    obj.format !== 'ookey' &&
    !('encrypted' in obj)
  ) {
    const items: ImportPreviewItem[] = []
    for (const row of obj.passwords) {
      const payload = googleRowToPayload(row)
      if (payload) items.push({ name: payload.name, payload, folderId: null })
    }
    if (items.length === 0) throw new ImportError('empty')

    return { format: 'google', items, newFolderNames: [] }
  }

  // OoKey native format detection
  if (obj.format === 'ookey' && Array.isArray(obj.items)) {
    const exp = parsed as OoKeyExport

    const items: ImportPreviewItem[] = exp.items
      .map(it => ({
        name: it.payload.name,
        payload: it.payload,
        folderId: it.folderId,
      }))
      .filter(it => it.name.trim().length > 0)

    if (items.length === 0) throw new ImportError('empty')

    return {
      format: 'ookey',
      items,
      newFolderNames: [],
    }
  }

  // Bitwarden format detection
  if ('encrypted' in obj && Array.isArray(obj.items)) {
    if (obj.encrypted === true) {
      throw new ImportError('encrypted_not_supported')
    }
    const bwExp = parsed as BitwardenExport

    const folderNameById = new Map<string, string>()
    for (const f of bwExp.folders ?? []) {
      folderNameById.set(f.id, f.name)
    }

    const existingByName = new Map<string, string>()
    for (const f of existingFolders) {
      existingByName.set(f.name, f.id)
    }

    const newFolderNames = new Set<string>()
    const items: ImportPreviewItem[] = []

    for (const it of bwExp.items) {
      const payload = bitwardenItemToPayload(it)
      if (!payload) continue

      let folderId: string | null = null
      const folderName = it.folderId ? folderNameById.get(it.folderId) : undefined
      if (folderName) {
        const existing = existingByName.get(folderName)
        if (existing) {
          folderId = existing
        } else {
          newFolderNames.add(folderName)
          // 後段で createFolder してから patch するため、preview 時点では null
        }
      }

      items.push({
        name: payload.name,
        payload,
        folderId,
      })
    }

    if (items.length === 0) throw new ImportError('empty')

    return {
      format: 'bitwarden',
      items,
      newFolderNames: [...newFolderNames],
    }
  }

  throw new ImportError('unknown_format')
}

export interface ExecuteImportResult {
  readonly created: number
  readonly foldersCreated: number
  readonly failed: number
  /** 重複検知でスキップした件数 (既存 vault と同じ + import file 内の duplicate)。 */
  readonly skipped: number
}

/**
 * 重複検知 key。type ごとに「実質的な一致」を表す最小フィールド集合を結合した
 * 文字列を返す。前後 whitespace は trim、case-insensitive。
 *
 * - login    : `login|<name>|<username>|<primary url>`
 * - card     : `card|<name>|<holder>|<last 4 of number>`
 * - note     : `note|<name>` (notes 本文を含めると微妙な差で別物扱いになるので含めない)
 * - identity : `identity|<name>|<firstName>|<lastName>`
 *
 * `name` のみだと "Gmail" のような同名カードを複数持つケースで意図せぬ skip に
 * なる恐れがあるので、type ごとに識別子になりやすい 1-2 フィールドを足している。
 */
export const dedupKey = (p: VaultItemPayload): string => {
  const norm = (s: string | null | undefined): string => (s ?? '').trim().toLowerCase()
  const name = norm(p.name)
  if (p.type === 'login') {
    const url = norm(p.uris?.[0]?.uri ?? p.url ?? '')

    return `login|${name}|${norm(p.username)}|${url}`
  }
  if (p.type === 'card') {
    const last4 = (p.cardNumber ?? '').replace(/\s+/gu, '').slice(-4)

    return `card|${name}|${norm(p.cardHolder)}|${last4}`
  }
  if (p.type === 'note') {
    return `note|${name}`
  }

  return `identity|${name}|${norm(p.firstName)}|${norm(p.lastName)}`
}

/**
 * Preview を確定 → 新規 folder を作成 → 各 item を `createVaultItemEncrypted` で
 * 順次保存。失敗は count して続行 (1 件失敗で全体 rollback はしない)。
 *
 * Bitwarden preview の `newFolderNames` 由来の item は、folder 作成後に folderId を
 * 解決し直す (folder name → id map を再構築)。
 */
export const executeImport = async (
  preview: ImportPreview,
  existingFolders: readonly { id: string; name: string }[],
): Promise<ExecuteImportResult> => {
  const session = sessionStore.getState()
  if (session.kind !== 'unlocked') {
    throw new ImportError('generic')
  }

  // 1) 新規フォルダ作成
  const folderByName = new Map<string, string>()
  for (const f of existingFolders) {
    folderByName.set(f.name, f.id)
  }

  let foldersCreated = 0
  for (const name of preview.newFolderNames) {
    if (folderByName.has(name)) continue
    try {
      const created = await createFolderEncrypted(session.symmetricKey, name)

      folderByName.set(name, created.id)
      foldersCreated += 1
    } catch {
      // フォルダ作成失敗時は対応 item を root に置く (folderId null)
    }
  }

  // 2) 重複検知の準備 — 既存 vault item を 1 回だけ取得して dedup key set 化
  // 失敗 (network 等) は propagate (item 作成も同様に失敗する状況のため)。
  const existingList = await loadVaultList(session.symmetricKey)
  const seenKeys = new Set<string>()
  for (const it of existingList.items) {
    seenKeys.add(dedupKey(it.payload))
  }

  // 3) item 作成 (既存重複 + import file 内重複の両方をスキップ)
  let created = 0
  let failed = 0
  let skipped = 0
  for (const item of preview.items) {
    const key = dedupKey(item.payload)
    if (seenKeys.has(key)) {
      skipped += 1
      continue
    }
    seenKeys.add(key)

    const folderId = item.folderId
    if (folderId === null && preview.format === 'bitwarden') {
      // Bitwarden 経路: folder name の解決を試みる (newFolderNames 経由)
      // 同名 folder を持つ item を再 walk するのは preview 側で `folderName` を持たないので
      // 一旦 root (null) で保存する。folder name 再利用の正確性は preview 設計を
      // 拡張する余地あり (deferred)。
    }
    try {
      await createVaultItemEncrypted(session.symmetricKey, item.payload, folderId)
      created += 1
    } catch {
      failed += 1
    }
  }

  return { created, foldersCreated, failed, skipped }
}
